import React from 'react';
import { View, Text, StyleSheet, ViewStyle } from 'react-native';
import { Inbox, type LucideIcon } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { Button } from './Button';
import { TextLink } from './TextLink';

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
  /** In-sheet / in-section register: smaller type, inline text action,
   * tighter padding. Full-screen empty states omit this. */
  compact?: boolean;
  style?: ViewStyle;
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  message,
  actionLabel,
  onAction,
  compact = false,
  style,
}: EmptyStateProps) {
  const { colors } = useTheme();

  return (
    <View style={[styles.container, compact && styles.containerCompact, style]}>
      <Icon size={compact ? 16 : 20} color={colors.textTertiary} style={styles.icon} />
      <Text style={[styles.title, compact && styles.titleCompact, { color: colors.text }]}>
        {title}
      </Text>
      {message && (
        <Text
          style={[
            styles.message,
            compact && styles.messageCompact,
            { color: colors.textSecondary },
          ]}>
          {message}
        </Text>
      )}
      {actionLabel &&
        onAction &&
        (compact ? (
          <TextLink label={actionLabel} onPress={onAction} style={styles.actionCompact} />
        ) : (
          <Button
            title={actionLabel}
            onPress={onAction}
            variant="primary"
            size="sm"
            style={styles.action}
          />
        ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACING.lg,
  },
  containerCompact: {
    paddingVertical: SPACING.md,
    paddingHorizontal: SPACING.sm,
  },
  icon: {
    marginBottom: SPACING.sm,
  },
  title: {
    ...TYPOGRAPHY.button,
    textAlign: 'center',
    marginBottom: SPACING.xs,
  },
  titleCompact: {
    ...TYPOGRAPHY.bodySmall,
    fontFamily: TYPOGRAPHY.h4.fontFamily,
    marginBottom: SPACING.xxs,
  },
  message: {
    ...TYPOGRAPHY.bodySmall,
    textAlign: 'center',
    maxWidth: 280,
  },
  messageCompact: {
    ...TYPOGRAPHY.caption,
    maxWidth: 260,
  },
  action: {
    marginTop: SPACING.md,
  },
  actionCompact: {
    marginTop: SPACING.sm,
  },
});
